import {Box, Button, Grid, makeStyles, TextField} from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import _ from 'lodash';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import LibraryAddOutlinedIcon from '@material-ui/icons/LibraryAddOutlined';
import TopBar from "../../components/Common/TopBar";
import CorrectionModal from "../../components/WrongAddressManagement/CorrectionModal";
import { showAlertSnackbar } from "../../reducers/uiSlice";
import { getAddress, getDeliveryTypes, getExistAddressTypes, getProvincesCovered } from "../../actions/wrongAddressActions";
import { DataGrid } from "@material-ui/data-grid";
import BatchCorrectionModal from "../../components/WrongAddressManagement/BatchCorrectionModal";
import { getUserPreference } from "../../utilities/common";
import { getProvinceCountryByWarehouse } from "../../actions/driverAppApprovalActions";
import { useToken } from "../../utilities/hooks";

const useStyles = makeStyles((theme)=> ({
    root: {
        backgroundColor: theme.palette.background.default,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: 0
    },
    appBarClasses: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        padding: theme.spacing(1)
    },
    topBarButton: {
        marginLeft: theme.spacing(2),
        color: 'unset',
        '&:hover': {
            color: theme.palette.text.primary
        }
    },
    buttonGroupStyle: {
        textAlign: 'right',
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2)
    },
}))

function WrongAddressTool() {
    const dispatch = useDispatch();
    const classes = useStyles();
    const {t} = useTranslation('wrongAddressTool');
    const tokenContents = useToken();

    const userUS = tokenContents.userUS;

    const userPref = getUserPreference();

    const columns = [
        {field: 'id', headerName: t('id'), hide: true},
        {
            field: 'address', 
            headerName: t('original_address'), 
            headerAlign: 'center',
            flex: 2,
            sortable: false,
            align: 'center'
        },
        {
            field: 'postal_code', 
            headerName: t('original_postal_code'), 
            flex: 1,
            headerAlign: 'center', 
            sortable: false,
            align: 'center'
        },
        {
            field: 'new_address', 
            headerName: t('corrective_address'), 
            flex: 2, 
            headerAlign: 'center', 
            sortable: false,
            align: 'center'
        },
        {
            field: 'new_postal_code', 
            headerName: t('postal_code'), 
            flex: 1, 
            headerAlign: 'center', 
            sortable: false,
            align: 'center'
        },
        {
            field: 'lngLat',
            headerName: t('lat_lng'),
            sortable: false,
            flex: 2,
            headerAlign: 'center', 
            align: 'center',
            renderCell: (params) => {
                return (
                    <div>
                        {params.row.lat},{params.row.lng}
                    </div>
                )
            }
        }
    ];

    const address = useRef();
    const postalCode = useRef();

    const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
    const [addressList, setAddressList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [batchCorrectionModalOpen, setBatchCorrectionModalOpen] = useState(false);
    const [resDetail, setResDetail] = useState({});
    const [type, setType] = useState();



    const handleCorrectionModalClose = () => {
        setCorrectionModalOpen(false);
    }

    const handleBatchCorrectionModalClose = () => {
        setBatchCorrectionModalOpen(false)
    }

    const handleSearch = () => {
        if(type === 'new') {
            return;
        }
        if(!address.current.value || !postalCode.current.value) {
            dispatch(showAlertSnackbar({message:t('address_postal_code_required'),type:'error'}));
            return;
        }
        setLoading(true);
        dispatch(getAddress({
            address: address.current.value,
            postal_code: postalCode.current.value
        }))
        .then((res) => {
            setAddressList([res]);
        })
        .catch((err) => {
            dispatch(showAlertSnackbar({message:err.message,type:'error'}));
        })
        .finally(() => {
            setLoading(false);
        })
    }

    const handleSelect = () => {
        setResDetail(addressList[0]);
        setType('detail');
        setCorrectionModalOpen(true);
    }



    useEffect(() => {
        dispatch(getExistAddressTypes());
        dispatch(getProvinceCountryByWarehouse(userPref.userCity));
        dispatch(getDeliveryTypes());
        dispatch(getProvincesCovered(userUS ? 'us' : 'ca'));
    }, [])


    return (
        <Box className={classes.root}>
            <TopBar 
                title={t('address_correction_tool')}
                buttons={[
                    {
                        id: 1,
                        title: t('pre_alerts'),
                        props: {
                            startIcon: <ReportProblemOutlinedIcon />,
                            onClick: ()=>{},
                            disabled: true
                        }
                    },
                    {
                        id: 2,
                        title: t('batch_correction'),
                        props: {
                            startIcon: <FileCopyOutlinedIcon />,
                            onClick: ()=>{
                                setBatchCorrectionModalOpen(true);
                            }
                        }
                    },
                    {
                        id: 3,
                        title: t('new_correction'),
                        props: {
                            startIcon: <LibraryAddOutlinedIcon  />,
                            onClick: ()=>{
                                setType('new');
                                setCorrectionModalOpen(true);
                            }
                        }
                    }
                ]}
                buttonClasses={classes.topBarButton}
                appBarClasses={classes.appBarClasses}
            />
            <Grid container style={{paddingTop:40,paddingLeft:20,paddingRight:20}}>
                <Grid item xs={6} style={{marginRight:20}}>
                    <TextField
                        fullWidth
                        inputRef={address}
                        variant="outlined"
                        label={t('address')}
                        id='wrongaddress-address-textfield'
                    />
                </Grid>
                <Grid item xs={2} style={{marginRight:40}}>
                    <TextField
                        fullWidth
                        inputRef={postalCode}
                        variant="outlined"
                        label={t('postal_code')}
                        id='wrongaddress-postal-code-textfield'
                    />
                </Grid>
                <Grid item xs={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        id='wrongaddress-search-button'
                        onClick={handleSearch}
                    >
                        {t('common:search')}
                    </Button>
                </Grid>
                <Grid item xs={12} style={{marginTop:40}}>
                    <DataGrid
                        style={{height: '70vh'}}
                        loading={loading}
                        alignItems="center"
                        rows={addressList || []}
                        columns={columns}
                        disableColumnFilter 
                        disableColumnMenu
                        pageSize={20}
                        rowsPerPageOptions={[20]}
                        isRowSelectable={false}
                        fullWidth
                        onSelectionModelChange={() => {
                            handleSelect();
                        }}
                    />
                </Grid>
            </Grid>
            <CorrectionModal
                open={correctionModalOpen}
                onClose={handleCorrectionModalClose}
                type={type}
                data={resDetail}
                handleSearch={handleSearch}
                isExist={true}
            />
            <BatchCorrectionModal
                open={batchCorrectionModalOpen}
                onClose={handleBatchCorrectionModalClose}
            />
        </Box>
    );
}

export default WrongAddressTool;